/**
 * Fundamental-domain engine (fundamental-domain-rewrite brief).
 *
 * The drawing unit is the WPG's *fundamental domain* — the minimal tile the
 * group's isometries replicate to fill the plane. The artist-convenient repeat
 * (square/hex/…) is assembled from N copies of the fundamental domain by the
 * group's point-group operations, then the lattice tiles those. This is the
 * correct model: the tile never draws into itself, and reflection groups
 * (p4m, p6m, …) are genuinely distinct from their rotation-only cousins.
 *
 * Every (fundamental domain + ops + lattice) here is validated against the same
 * coverage + round-trip contract as the rest of the engine
 * (tests/fundamentalDomain.test.ts) — the geometry below is derived, not taken
 * on faith from the brief.
 */
import {
  IDENTITY,
  compose,
  glideLine,
  matTransform,
  reflectionLine,
  rotationAbout,
  scale,
  translation,
  type Mat,
} from '@/geometry/affine';
import { wrapPointGeneric } from '@/geometry/wrap';
import type { SymmetryVariant, TileTransform } from '@/patterns/types';
import { normaliseParams, type LatticeParams } from './engine';
import { getWallpaperGroup, type WallpaperGroupId } from './groups';

type Pt = [number, number];

const DEG = Math.PI / 180;
/** Rotation by `deg` about the cell centre (0.5, 0.5). */
const Rot = (deg: number): Mat => rotationAbout(deg * DEG, 0.5, 0.5);
/** Reflection across the line through the centre at angle `deg`. */
const Mir = (deg: number): Mat => reflectionLine(0.5, 0.5, Math.cos(deg * DEG), Math.sin(deg * DEG));

const SQRT3 = Math.sqrt(3);

/** width×height box centred in [0,1]², longest side = span. */
function fitBox(aspect: number, span = 0.9): { w: number; h: number } {
  return aspect >= 1 ? { w: span / aspect, h: span } : { w: span, h: span * aspect };
}

interface Spec {
  /** the fundamental-domain polygon (the drawing tile) */
  polygon: Pt[];
  /** lattice translation vectors */
  a: Pt;
  b: Pt;
  /** point-group coset isometries; {op·polygon} partitions the artist cell */
  ops: Mat[];
}

// ── per-lattice cell geometry ────────────────────────────────────────────────

function rectCell(params: LatticeParams): { x0: number; y0: number; w: number; h: number } {
  const { w, h } = fitBox(params.aspectRatio ?? 1.6);
  return { x0: 0.5 - w / 2, y0: 0.5 - h / 2, w, h };
}

function parallelogram(params: LatticeParams): { poly: Pt[]; a: Pt; b: Pt } {
  const aspect = params.aspectRatio ?? 1;
  const theta = (params.angle ?? 75) * DEG;
  let ax = 1;
  let bx = aspect * Math.cos(theta);
  let by = aspect * Math.sin(theta);
  const spanX = Math.max(ax, ax + bx, bx, 0) - Math.min(0, bx);
  const spanY = Math.abs(by);
  const scale = 0.9 / Math.max(spanX, spanY);
  ax *= scale;
  bx *= scale;
  by *= scale;
  const ox = 0.5 - (ax + bx) / 2;
  const oy = 0.5 - by / 2;
  return {
    poly: [
      [ox, oy],
      [ox + ax, oy],
      [ox + ax + bx, oy + by],
      [ox + bx, oy + by],
    ],
    a: [ax, 0],
    b: [bx, by],
  };
}

function rhombus(params: LatticeParams): { L: Pt; T: Pt; R: Pt; B: Pt; a: Pt; b: Pt } {
  const theta = (params.angle ?? 60) * DEG;
  const hx0 = Math.cos(theta / 2);
  const hy0 = Math.sin(theta / 2);
  const scale = 0.48 / Math.max(hx0, hy0);
  const hx = hx0 * scale;
  const hy = hy0 * scale;
  return {
    L: [0.5 - hx, 0.5],
    T: [0.5, 0.5 - hy],
    R: [0.5 + hx, 0.5],
    B: [0.5, 0.5 + hy],
    a: [hx, -hy],
    b: [hx, hy],
  };
}

/** Regular hexagon vertices (pointy-top) + lattice, centred at (0.5,0.5). */
function hexCell(): { v: Pt[]; a: Pt; b: Pt } {
  const H = SQRT3 / 4;
  return {
    v: [
      [0.5, 0],
      [0.5 + H, 0.25],
      [0.5 + H, 0.75],
      [0.5, 1],
      [0.5 - H, 0.75],
      [0.5 - H, 0.25],
    ],
    a: [2 * H, 0],
    b: [H, 0.75],
  };
}

const mid = (p: Pt, q: Pt): Pt => [(p[0] + q[0]) / 2, (p[1] + q[1]) / 2];

// ── the spec per wallpaper group ─────────────────────────────────────────────

function spec(wpgId: WallpaperGroupId, params: LatticeParams): Spec {
  switch (wpgId) {
    // ── Oblique ──
    case 'p1': {
      const { poly, a, b } = parallelogram(params);
      return { polygon: poly, a, b, ops: [IDENTITY] };
    }
    case 'p2': {
      const { poly, a, b } = parallelogram(params);
      return { polygon: [poly[0]!, poly[1]!, poly[2]!], a, b, ops: [IDENTITY, Rot(180)] };
    }

    // ── Rectangular (symmorphic) ──
    case 'pm': {
      const { x0, y0, w, h } = rectCell(params);
      const fd: Pt[] = [
        [x0, y0],
        [0.5, y0],
        [0.5, y0 + h],
        [x0, y0 + h],
      ];
      return { polygon: fd, a: [w, 0], b: [0, h], ops: [IDENTITY, Mir(90)] };
    }
    case 'pmm': {
      const { x0, y0, w, h } = rectCell(params);
      const fd: Pt[] = [
        [x0, y0],
        [0.5, y0],
        [0.5, 0.5],
        [x0, 0.5],
      ];
      return { polygon: fd, a: [w, 0], b: [0, h], ops: [IDENTITY, Mir(90), Mir(0), Rot(180)] };
    }

    // ── Rectangular (non-symmorphic: glides) ──
    case 'pg': {
      const { x0, y0, w, h } = rectCell(params);
      const fd: Pt[] = [
        [x0, y0],
        [0.5, y0],
        [0.5, y0 + h],
        [x0, y0 + h],
      ];
      // glide along the vertical axis by half the vertical period
      return { polygon: fd, a: [w, 0], b: [0, h], ops: [IDENTITY, glideLine(0.5, 0.5, 0, 1, h / 2)] };
    }
    case 'pgg': {
      const { x0, y0, w, h } = rectCell(params);
      const fd: Pt[] = [
        [x0, y0],
        [0.5, y0],
        [0.5, 0.5],
        [x0, 0.5],
      ];
      // perpendicular glide axes at the quarter-lines (not the centre)
      const gx = glideLine(0.5, y0 + h / 4, 1, 0, w / 2); // axis y = y0+h/4, glide w/2 in x
      const gy = glideLine(x0 + w / 4, 0.5, 0, 1, h / 2); // axis x = x0+w/4, glide h/2 in y
      return { polygon: fd, a: [w, 0], b: [0, h], ops: [IDENTITY, gx, gy, compose(gx, gy)] };
    }
    case 'pmg': {
      const { x0, y0, w, h } = rectCell(params);
      const fd: Pt[] = [
        [x0, y0],
        [0.5, y0],
        [0.5, 0.5],
        [x0, 0.5],
      ];
      // vertical mirror + horizontal glide + their composition
      const mV = Mir(90);
      const gx = glideLine(0.5, 0.5, 1, 0, w / 2);
      return { polygon: fd, a: [w, 0], b: [0, h], ops: [IDENTITY, mV, gx, compose(mV, gx)] };
    }

    // ── Centred-rectangular / rhombic (symmorphic) ──
    case 'cm': {
      const { L, R, B, a, b } = rhombus(params);
      return { polygon: [L, R, B], a, b, ops: [IDENTITY, Mir(0)] };
    }
    case 'cmm': {
      const { R, B, a, b } = rhombus(params);
      return { polygon: [[0.5, 0.5], R, B], a, b, ops: [IDENTITY, Mir(0), Mir(90), Rot(180)] };
    }

    // ── Square ──
    case 'p4': {
      const { w } = fitBox(1);
      const x0 = 0.5 - w / 2;
      const A: Pt = [x0, x0];
      const B: Pt = [x0 + w, x0];
      return {
        polygon: [[0.5, 0.5], A, B],
        a: [w, 0],
        b: [0, w],
        ops: [IDENTITY, Rot(90), Rot(180), Rot(270)],
      };
    }
    case 'p4m': {
      const { w } = fitBox(1);
      const x0 = 0.5 - w / 2;
      const Mb: Pt = [0.5, x0];
      const B: Pt = [x0 + w, x0];
      return {
        polygon: [[0.5, 0.5], Mb, B],
        a: [w, 0],
        b: [0, w],
        ops: [IDENTITY, Rot(90), Rot(180), Rot(270), Mir(0), Mir(90), Mir(45), Mir(135)],
      };
    }

    case 'p4g': {
      // International Tables (No. 12) general positions, origin at the 4-fold
      // corner. Built in cell-fraction coords and conjugated into tile space —
      // the (5)–(8) glides carry the half-cell translation that distinguishes
      // p4g from p4m. FD = 1/8-cell 45/45/90 triangle at the corner.
      const { w } = fitBox(1);
      const x0 = 0.5 - w / 2;
      const phi = compose(translation(x0, x0), scale(w, w));
      const phiInv = compose(scale(1 / w, 1 / w), translation(-x0, -x0));
      const frac: Mat[] = [
        [1, 0, 0, 1, 0, 0],
        [-1, 0, 0, -1, 0, 0],
        [0, 1, -1, 0, 0, 0],
        [0, -1, 1, 0, 0, 0],
        [-1, 0, 0, 1, 0.5, 0.5],
        [1, 0, 0, -1, 0.5, 0.5],
        [0, -1, -1, 0, 0.5, 0.5],
        [0, 1, 1, 0, 0.5, 0.5],
      ];
      const ops = frac.map((o) => compose(phi, compose(o, phiInv)));
      // FD = the 45/45/90 triangle on one side of the x+y=1/2 glide axis (right
      // angle at the corner); the half-quadrant triangle that touches the axis
      // is fixed by op (8), so it is NOT a fundamental domain.
      const fd: Pt[] = [
        [x0, x0],
        [x0 + w / 2, x0],
        [x0, x0 + w / 2],
      ];
      return { polygon: fd, a: [w, 0], b: [0, w], ops };
    }

    // ── Hexagonal (rotation only) ──
    case 'p3': {
      const { v, a, b } = hexCell();
      // 120° rhombus sector (centre + three consecutive vertices); R120/R240
      // place the other two sectors → one hexagon, stabilizerOrder 1.
      // Bug 0: `rhombusRotated` (default true for new configs, see engine.ts)
      // shifts which three consecutive vertices form the wedge by one step —
      // adjacent hex vertices are exactly 60° apart, so starting at v5
      // instead of v0 rotates the whole wedge 60° anticlockwise on screen
      // (v0..v5 run clockwise: -90°, -30°, 30°, 90°, 150°, -150°).
      const i0 = params.rhombusRotated ? 5 : 0;
      const wedge = [v[i0]!, v[(i0 + 1) % 6]!, v[(i0 + 2) % 6]!];
      return { polygon: [[0.5, 0.5], ...wedge], a, b, ops: [IDENTITY, Rot(120), Rot(240)] };
    }
    case 'p6': {
      const { v, a, b } = hexCell();
      // 60° equilateral wedge
      return {
        polygon: [[0.5, 0.5], v[0]!, v[1]!],
        a,
        b,
        ops: [IDENTITY, Rot(60), Rot(120), Rot(180), Rot(240), Rot(300)],
      };
    }

    // ── Hexagonal (with mirrors) ──
    case 'p3m1': {
      // mirror axes through the vertices; FD = 60° equilateral wedge between two
      const { v, a, b } = hexCell();
      return {
        polygon: [[0.5, 0.5], v[0]!, v[1]!],
        a,
        b,
        ops: [IDENTITY, Rot(120), Rot(240), Mir(30), Mir(90), Mir(150)],
      };
    }
    case 'p31m': {
      // mirror axes through the edge midpoints; FD = the kite between two of them
      const { v, a, b } = hexCell();
      return {
        polygon: [[0.5, 0.5], mid(v[0]!, v[1]!), v[1]!, mid(v[1]!, v[2]!)],
        a,
        b,
        ops: [IDENTITY, Rot(120), Rot(240), Mir(0), Mir(60), Mir(120)],
      };
    }
    case 'p6m': {
      // full D6; FD = 30/60/90 wedge (1/12 of the hexagon)
      const { v, a, b } = hexCell();
      return {
        polygon: [[0.5, 0.5], v[0]!, mid(v[0]!, v[1]!)],
        a,
        b,
        ops: [
          IDENTITY,
          Rot(60),
          Rot(120),
          Rot(180),
          Rot(240),
          Rot(300),
          Mir(0),
          Mir(30),
          Mir(60),
          Mir(90),
          Mir(120),
          Mir(150),
        ],
      };
    }

    default:
      throw new Error(`fundamental-domain spec not yet implemented for ${wpgId}`);
  }
}

/** How the drawing tile is presented (fundamental-domain-engine addendum §1). */
export type ArtistTile = 'standard' | 'hexagon' | 'rhombus' | 'parallelogram';

const par2 = (n: number, m: number): number => (((n - m) % 2) + 2) % 2;
const par3 = (n: number, m: number): number => (((n - m) % 3) + 3) % 3;

/**
 * Artist-tile generator: ONE placement per lattice cell. The drawing appears
 * exactly once within each tile; what varies *between* cells is a rotation (or
 * reflection) chosen by the cell's class — so neighbours show the same drawing
 * turned, never a duplicate inside one tile (stabilizerOrder 1). The cell op is
 * about the origin centre (0.5,0.5); the tile polygon is centred and
 * rotation-symmetric, so its outline is unchanged and the tiles still tile.
 */
/**
 * Bug: colour tiling (brief §4) reads `t.latticeIndex`/`t.opIndex` off each
 * transform (see colourGroups.ts's classifyColour) to decide which sublattice
 * colour a placement gets — but this generator (hexagon/rhombus/parallelogram
 * artist tiles) never set them, only genFrom() (the "standard" tile path)
 * did. So colour tiling silently no-opped for every pattern using one of
 * these artist tiles (hex-p1, hex-p3, rho-p1, para-p2, and any custom
 * pattern built on them) — classifyColour bails to `null` on missing
 * metadata, so every placement rendered in the main colour only.
 *
 * `classOf(n, m)` already IS the correct opIndex: it's the index into
 * whatever discrete op set `cellOp` can produce, and for every group that
 * reaches this generator (p1, p2, p3's hexagon tile) that set is built in the
 * same order as the corresponding spec()'s `ops` array (e.g. hex-p3's
 * `Rot(c*120)` for c=0,1,2 matches spec('p3',…).ops exactly). (n, m) are the
 * same translation-lattice indices genFrom() uses, since both walk the same
 * `a`/`b` basis.
 */
function artistTileGen(
  a: Pt,
  b: Pt,
  classOf: (n: number, m: number) => number,
  cellOp: (cls: number) => Mat,
): (rings: number) => TileTransform[] {
  const [ax, ay] = a;
  const [bx, by] = b;
  return (rings: number): TileTransform[] => {
    const out: TileTransform[] = [];
    for (let m = -rings; m <= rings; m++) {
      for (let n = -rings; n <= rings; n++) {
        const cls = classOf(n, m);
        const tt = matTransform(compose(translation(n * ax + m * bx, n * ay + m * by), cellOp(cls)));
        tt.latticeIndex = [n, m];
        tt.opIndex = cls;
        out.push(tt);
      }
    }
    return out;
  };
}

/** (cell class, per-cell op) for a hexagon artist tile, keyed by base group. */
function hexArtist(wpgId: WallpaperGroupId): {
  classOf: (n: number, m: number) => number;
  cellOp: (c: number) => Mat;
} {
  switch (wpgId) {
    case 'p3':
      return { classOf: par3, cellOp: (c) => Rot(c * 120) };
    default: // p1 — sliding, every tile identical
      return { classOf: () => 0, cellOp: () => IDENTITY };
  }
}

function genFrom(s: Spec): (rings: number) => TileTransform[] {
  const [ax, ay] = s.a;
  const [bx, by] = s.b;
  return (rings: number): TileTransform[] => {
    const out: TileTransform[] = [];
    for (let m = -rings; m <= rings; m++) {
      for (let n = -rings; n <= rings; n++) {
        const t = translation(n * ax + m * bx, n * ay + m * by);
        for (let opIndex = 0; opIndex < s.ops.length; opIndex++) {
          const tt = matTransform(compose(t, s.ops[opIndex]!));
          tt.latticeIndex = [n, m];
          tt.opIndex = opIndex;
          out.push(tt);
        }
      }
    }
    return out;
  };
}

export function buildFundamentalVariant(
  wpgId: WallpaperGroupId,
  rawParams: LatticeParams,
  artistTile: ArtistTile = 'standard',
): { polygon: Pt[]; variant: SymmetryVariant } {
  const group = getWallpaperGroup(wpgId);
  const params = normaliseParams(group.lattice, rawParams);

  let polygon: Pt[];
  let gen: (rings: number) => TileTransform[];
  let basis: { a: Pt; b: Pt };

  if (artistTile === 'hexagon') {
    // One drawing per hexagon; neighbours rotated/mirrored by the honeycomb's
    // 3-colouring. Only 3-fold-based groups (p1/p3/p3m1/p31m) work this way —
    // true 6-fold (p6) has no coherent one-per-hexagon assignment, so it is not
    // offered as a hexagon tile (use the triangular tri-p6 instead).
    const { v, a, b } = hexCell();
    const { classOf, cellOp } = hexArtist(wpgId);
    polygon = v;
    gen = artistTileGen(a, b, classOf, cellOp);
    basis = { a, b };
  } else if (artistTile === 'rhombus' && wpgId !== 'p3') {
    // Rhombus sliding (p1): one rhombus per cell, no rotation.
    const r = rhombus(params);
    polygon = [r.L, r.T, r.R, r.B];
    gen = artistTileGen(r.a, r.b, () => 0, () => IDENTITY);
    basis = { a: r.a, b: r.b };
  } else if (artistTile === 'parallelogram') {
    // Whole parallelogram as the drawing tile; p2 alternates 0°/180° by cell.
    const p = parallelogram(params);
    polygon = p.poly;
    const half = wpgId === 'p2';
    const classOf = half ? par2 : () => 0;
    const cellOp = (c: number): Mat => (c === 1 ? Rot(180) : IDENTITY);
    gen = artistTileGen(p.a, p.b, classOf, cellOp);
    basis = { a: p.a, b: p.b };
  } else {
    // Standard fundamental domain — and rhombus+p3, whose 120° sector already
    // assembles the isometric cube (three distinct rhombi per hexagon).
    const s = spec(artistTile === 'rhombus' ? 'p3' : wpgId, params);
    polygon = s.polygon;
    gen = genFrom(s);
    basis = { a: s.a, b: s.b };
  }

  const variant: SymmetryVariant = {
    id: `fd:${wpgId}:${artistTile}`,
    name: group.label,
    description: group.blurb,
    stabilizerOrder: 1,
    transformsInRange: gen,
    latticeBasis: basis,
    wrapPoint: (x, y) => wrapPointGeneric(polygon, gen(3), x, y),
  };
  return { polygon, variant };
}

function dist(a: Pt, b: Pt): number {
  return Math.hypot(a[0] - b[0], a[1] - b[1]);
}

/** Are all of a polygon's sides the same length (within a loose relative
 *  tolerance — lattice params are floats assembled from trig, not exact)? */
function hasEqualSides(polygon: Pt[], tolerance = 0.03): boolean {
  const n = polygon.length;
  const sides = polygon.map((p, i) => dist(p, polygon[(i + 1) % n]!));
  const avg = sides.reduce((a, b) => a + b, 0) / n;
  return avg > 0 && sides.every((s) => Math.abs(s - avg) < avg * tolerance);
}

/**
 * The canvas-rotation snap step for the CURRENT drawing tile's actual shape
 * (brief 11-layout2 §1, user correction — "it's about the symmetry of the
 * shape", not the wallpaper group's abstract point-group order or lattice
 * family: some triangles want 60°, some 45°, depending on whether they're
 * right-angled/isosceles or equilateral).
 *
 * A regular hexagon (the hexagon artist tile, any base group — always a
 * fixed regular hexagon, see hexCell()) has a natural 60° grid. An
 * equilateral triangle (e.g. p6's standard fundamental domain — two
 * hexagon-radius sides plus one hexagon-side, and a regular hexagon's
 * circumradius equals its side length, so these three sides are always
 * equal) tessellates by alternating up/down copies, which also realigns
 * every 60°. Every other shape actually produced by this engine — a
 * right-angled or otherwise non-equilateral triangle (p4/p4m/p4g's 45/45/90
 * corner triangle, etc.), a square, rectangle, rhombus, or parallelogram —
 * has no finer natural grid than 45°, the sensible uniform default.
 *
 * Takes the polygon directly (not a WallpaperGroupId) because the same
 * group can present a very different shape depending on artist tile and
 * lattice params (e.g. p1 "Sliding" has no rotational symmetry of its own,
 * but its hexagon artist tile is a genuine regular hexagon) — shared by the
 * (disabled) two-finger gesture snap and the Tessellation panel's rotate
 * CW/CCW buttons so both agree on the same step for whatever's on screen.
 */
export function rotationStepRadians(polygon: Pt[]): number {
  const isTriangleOrHexagon = polygon.length === 3 || polygon.length === 6;
  const degrees = isTriangleOrHexagon && hasEqualSides(polygon) ? 60 : 45;
  return (degrees * Math.PI) / 180;
}
